import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { convertTeamNameToJerseyFormat, type Sport } from '@/utils/jersey-mapping';
import { TEAMS } from '@/utils/teams';

const CDN_BASE_URL = 'https://bet99pickemdev.blob.core.windows.net';

const JERSEY_NUMBERS = Array.from({ length: 100 }, (_, i) => i.toString());

type JerseySport = Sport | 'NBA' | 'NCAA' | 'MLB' | 'UCL' | 'WORLD_CUP';

interface JerseySelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string) => void;
  fieldLabel?: string;
}

export function JerseySelectorModal({
  open,
  onOpenChange,
  onSelect,
  fieldLabel = 'Image',
}: JerseySelectorModalProps) {
  const [selectedSport, setSelectedSport] = useState<JerseySport>('NHL');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedProp, setSelectedProp] = useState<string>('');
  const [selectedNumber, setSelectedNumber] = useState<string>('blank');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [imageError, setImageError] = useState(false);

  // Reset team selection when sport changes
  useEffect(() => {
    setSelectedTeam(selectedTeam);
  }, [selectedSport]);

  useEffect(() => {
    let url: string;

    if (selectedSport === 'PROPS') {
      const propName = selectedProp.replace(/\s+/g, '_').toLowerCase();

      url = `${CDN_BASE_URL}/props/${propName}_prop.svg`;

      setPreviewUrl(url);
      setImageError(false);
    } else if (selectedTeam) {
      const teamName =
        selectedSport === 'NFL' ? selectedTeam : convertTeamNameToJerseyFormat(selectedTeam);

      let url = `${CDN_BASE_URL}/jerseys/${teamName}`;
      if (selectedSport === 'OLYMPICS') {
        url = `${CDN_BASE_URL}/jerseys/olympics/${teamName}`;
      }

      if (selectedSport === 'NCAA') {
        url = `${CDN_BASE_URL}/jerseys/${teamName}`;
      }

      if (selectedNumber && selectedNumber !== 'blank') {
        url += `_${selectedNumber}`;
      }

      if (selectedSport === 'WORLD_CUP') {
        url += '.svg';
      } else {
        url += '.png';
      }

      setPreviewUrl(url);
      setImageError(false);
    } else {
      setPreviewUrl('');
    }
  }, [selectedTeam, selectedNumber, selectedSport, selectedProp]);

  const handleSelect = () => {
    if (previewUrl) {
      onSelect(previewUrl);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedSport('NHL');
    setSelectedTeam('');
    setSelectedNumber('blank');
    setSelectedProp('');
    setPreviewUrl('');
    setImageError(false);
    onOpenChange(false);
  };

  const formatTeamName = (teamKey: string) => {
    return teamKey.replace(/_/g, ' ');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Select Jersey {fieldLabel && `for ${fieldLabel}`}</DialogTitle>
          <DialogDescription>Choose a sport, team, and optional jersey number</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="sport-select">Sport</Label>
            <Select
              value={selectedSport}
              onValueChange={(value: JerseySport) => setSelectedSport(value)}
            >
              <SelectTrigger id="sport-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NHL">NHL (Hockey)</SelectItem>
                <SelectItem value="NFL">NFL (Football)</SelectItem>
                <SelectItem value="PROPS">Props</SelectItem>
                <SelectItem value="OLYMPICS">Olympics</SelectItem>
                <SelectItem value="NBA">NBA</SelectItem>
                <SelectItem value="NCAA">NCAA</SelectItem>
                <SelectItem value="MLB">MLB</SelectItem>
                <SelectItem value="UCL">UCL</SelectItem>
                <SelectItem value="WORLD_CUP">World Cup</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(selectedSport === 'NFL' ||
            selectedSport === 'NHL' ||
            selectedSport === 'OLYMPICS' ||
            selectedSport === 'NBA' ||
            selectedSport === 'MLB' ||
            selectedSport === 'NCAA' ||
            selectedSport === 'UCL' ||
            selectedSport === 'WORLD_CUP') && (
            <div className="space-y-2">
              <Label htmlFor="team-select">Team</Label>
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger id="team-select">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {TEAMS[selectedSport].map(team => (
                    <SelectItem key={team} value={team}>
                      {formatTeamName(team)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedSport === 'PROPS' && (
            <div className="space-y-2">
              <Label htmlFor="prop-select">Props</Label>
              <Select value={selectedProp} onValueChange={setSelectedProp}>
                <SelectTrigger id="prop-select">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {TEAMS.PROPS.map(prop => (
                    <SelectItem key={prop} value={prop}>
                      {formatTeamName(prop)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedTeam && (
            <div className="space-y-2">
              <Label htmlFor="number-select">Jersey Number (Optional)</Label>
              <Select value={selectedNumber} onValueChange={setSelectedNumber}>
                <SelectTrigger id="number-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="blank">Blank (No Number)</SelectItem>
                  {JERSEY_NUMBERS.map(num => (
                    <SelectItem key={num} value={num}>
                      #{num}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* {selectedTeam && selectedSport === 'NFL' && (
            <p className="text-sm text-muted-foreground">
              NFL teams use a single jersey design for all players
            </p>
          )} */}

          {previewUrl && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="relative w-full h-64 border rounded-md overflow-hidden bg-gray-50">
                {!imageError ? (
                  <img
                    src={previewUrl}
                    alt="Jersey preview"
                    className="w-full h-full object-contain"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex w-full h-full items-center justify-center text-sm text-muted-foreground">
                    Failed to load image
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground break-all">{previewUrl}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={!previewUrl || imageError}>
            Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
